import React from 'react';
import { Music, Paperclip, ExternalLink, FileText } from 'lucide-react';

const ProgramViewerMode = ({ sections, getSongMeta }) => {
  if (!sections || sections.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No program items yet.</p>
      </div>
    );
  }

  const multiSection = sections.length > 1;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="program-viewer-mode">
      <div className="space-y-5">
        {sections.map((section) => {
          const sectionItems = section.items || [];
          const sectionTitle = section.header?.title || 'Section';
          // Show fieldset for all sections when multiple exist;
          // for the "Default" placeholder, display it as "General" in that case.
          const showFieldset = multiSection || sectionTitle !== 'Default';
          const legendLabel = sectionTitle === 'Default' ? 'General' : sectionTitle;
          return (
            <fieldset
              key={section.id}
              className={showFieldset ? 'border border-slate-300 rounded-lg p-0 overflow-hidden' : 'p-0'}
              data-testid={`viewer-section-${section.id}`}
            >
              {showFieldset && (
                <legend className="ml-4 px-2 text-sm font-semibold text-slate-700">
                  {legendLabel}
                </legend>
              )}
              {sectionItems.length === 0 ? (
                <p className="text-xs text-slate-400 px-5 pb-4 pt-1">No items in this section</p>
              ) : (
                <ol className="list-none px-5 pb-4 pt-1 space-y-3">
                  {sectionItems.map((item, itemIndex) => {
                    const itemSongs = item.songs || [];
                    const itemAttachments = item.attachments || [];
                    const itemLinks = item.links || [];
                    const hasDetails = itemSongs.length > 0 || itemAttachments.length > 0 || itemLinks.length > 0;

                    return (
                      <li key={item.id} data-testid={`viewer-item-${item.id}`}>
                        <div className="flex items-baseline gap-2.5">
                          <span className="text-sm font-semibold text-green-700 flex-shrink-0 w-6 text-right">
                            {itemIndex + 1}.
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900">{item.name || item.title}</p>
                          </div>
                        </div>
                        {hasDetails && (
                          <ul className="ml-9 mt-1.5 space-y-1">
                            {itemSongs.map((s) => {
                              const meta = getSongMeta(s.song_id);
                              const title = s.title || meta.title || 'Unknown';
                              const key = meta.key;
                              const tempo = meta.bpm;
                              return (
                                <li key={s.song_id} className="flex items-start gap-2 text-xs text-slate-600">
                                  <Music className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                                  <span className="break-words">
                                    {title}
                                    {key && <span className="text-slate-400 ml-1">&middot; {key}</span>}
                                  </span>
                                </li>
                              );
                            })}
                            {itemAttachments.map((att) => (
                              <li key={att.attachment_id || att.filename} className="flex items-center gap-2 text-xs text-slate-600">
                                <Paperclip className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span>{att.label || att.filename}</span>
                              </li>
                            ))}
                            {itemLinks.map((link) => (
                              <li key={link.id} className="flex items-center gap-2 text-xs text-slate-600">
                                <ExternalLink className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                <a href={link.url} target="_blank" rel="noopener noreferrer"
                                  className="hover:text-green-700 hover:underline truncate">
                                  {link.label || link.title || link.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                        {item.notes && <p className="text-xs text-slate-500 mt-1.5 ml-9 whitespace-pre-wrap">{item.notes}</p>}
                      </li>
                    );
                  })}
                </ol>
              )}
            </fieldset>
          );
        })}
      </div>
    </div>
  );
};

export default ProgramViewerMode;
